import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

// ============================================================================
// FieldGroup
// ----------------------------------------------------------------------------
// Collapsible section heading for the detail page. Default expanded; click
// the chevron to collapse. The choice is local component state — collapsing
// state is not persisted across navigation.
// ============================================================================

interface Props {
    title: string;
    description?: string;
    children: ReactNode;
    defaultOpen?: boolean;
    accentColor?: string;  // Tailwind class like 'text-pt-gold'
}

export default function FieldGroup({
    title,
    description,
    children,
    defaultOpen = true,
    accentColor = 'text-pt-gold'
}: Props) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <section className="bg-pt-panel border border-pt-border rounded-lg">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-pt-dark/30 transition-colors rounded-t-lg"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <span className={accentColor}>
                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-sm font-display font-semibold tracking-wide uppercase">
                            {title}
                        </h3>
                        {description && (
                            <div className="text-xs text-pt-text-dim mt-0.5">{description}</div>
                        )}
                    </div>
                </div>
            </button>

            {open && (
                <div className="px-5 pb-4 border-t border-pt-border divide-y divide-pt-border/50">
                    {children}
                </div>
            )}
        </section>
    );
}
